<script setup>
  import { computed, watch, ref } from 'vue';
  import { useLayoutStore } from '@/stores/layout';
  import AppTopbar from './AppTopbar.vue';
  import AppFooter from './AppFooter.vue';
  import AppSidebar from './AppSidebar.vue';
  import TopMenu from '@/components/TopMenu.vue'; // 추가
  import AppBreadcrumb from '@/components/AppBreadcrumb.vue'; // 추가
  import MDIContainer from '@/components/MDIContainer.vue'; // MDI 컨테이너 추가
  import { useLayout } from '@/layout/composables/layout';

  const { layoutConfig, layoutState, isSidebarActive } = useLayout();
  const layoutStore = useLayoutStore();
  const outsideClickListener = ref(null);

  watch(isSidebarActive, (newVal) => {
    if (newVal) {
      bindOutsideClickListener();
    } else {
      unbindOutsideClickListener();
    }
  });

  const containerClass = computed(() => {
    return {
      'layout-theme-light': layoutConfig.darkTheme.value === 'light',
      'layout-theme-dark': layoutConfig.darkTheme.value === 'dark',
      'layout-overlay': layoutConfig.menuMode.value === 'overlay',
      'layout-static':
        layoutConfig.menuMode.value === 'static' &&
        layoutStore.layoutMode === 'sidebar',
      'layout-static-inactive':
        layoutState.staticMenuDesktopInactive.value &&
        layoutConfig.menuMode.value === 'static',
      'layout-overlay-active': layoutState.overlayMenuActive.value,
      'layout-mobile-active': layoutState.staticMenuMobileActive.value,
      'p-ripple-disabled': layoutConfig.ripple.value === false,
      'layout-top': layoutStore.layoutMode === 'top',
      'layout-fullscreen': layoutStore.isFullScreen, // 전체화면 모드 클래스 추가
    };
  });
  const bindOutsideClickListener = () => {
    if (!outsideClickListener.value) {
      outsideClickListener.value = (event) => {
        if (isOutsideClicked(event)) {
          layoutState.overlayMenuActive.value = false;
          layoutState.staticMenuMobileActive.value = false;
          layoutState.menuHoverActive.value = false;
        }
      };
      document.addEventListener('click', outsideClickListener.value);
    }
  };
  const unbindOutsideClickListener = () => {
    if (outsideClickListener.value) {
      document.removeEventListener('click', outsideClickListener);
      outsideClickListener.value = null;
    }
  };
  const isOutsideClicked = (event) => {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.layout-menu-button');

    return !(
      sidebarEl.isSameNode(event.target) ||
      sidebarEl.contains(event.target) ||
      topbarEl.isSameNode(event.target) ||
      topbarEl.contains(event.target)
    );
  };

  // 전체화면 모드 종료 핸들러
  const exitFullScreen = () => {
    layoutStore.setFullScreen(false);
  };
</script>

<template>
  <div
    class="layout-wrapper"
    :class="containerClass"
  >
    <!-- 전체화면 모드가 아닐 때만 표시할 요소들 -->
    <template v-if="!layoutStore.isFullScreen">
      <app-topbar></app-topbar>
      <div
        class="layout-sidebar"
        v-if="layoutStore.layoutMode === 'sidebar'"
      >
        <app-sidebar></app-sidebar>
      </div>
      <TopMenu v-if="layoutStore.layoutMode === 'top'" />
    </template>

    <div class="layout-main-container">
      <div class="layout-main">
        <MDIContainer>
          <template #header>
            <!-- 전체화면 모드일 때는 돌아가기 버튼 표시 -->
            <div
              v-if="layoutStore.isFullScreen"
              class="fullscreen-header"
            >
              <Button
                icon="pi pi-arrow-left"
                @click="exitFullScreen"
                label="돌아가기"
                class="p-button-text"
              />
            </div>
            <!-- 일반 모드일 때는 Breadcrumb 표시 -->
            <AppBreadcrumb v-else />
          </template>
        </MDIContainer>
      </div>
      <!-- 전체화면 모드가 아닐 때만 푸터 표시 -->
      <app-footer v-if="!layoutStore.isFullScreen"></app-footer>
    </div>
    <div class="layout-mask"></div>
  </div>
</template>

<style lang="scss">
  .layout-wrapper {
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .layout-main-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      min-height: 0; // 추가: flex container의 최소 높이 제한
      margin: 0; // 추가: 기본 마진 제거
      padding: 0; // 추가: 기본 패딩 제거
      padding-top: 4rem; // 모든 모드에서 상단 여백 4rem 기본 설정

      .layout-main {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        min-height: 0; // 추가: flex container의 최소 높이 제한
        margin: 0; // 추가: 마진 제거
      }

      // footer 스타일 추가
      .layout-footer {
        margin-top: auto; // footer를 하단에 고정
        flex-shrink: 0; // footer 크기 고정
      }
    }

    // 기존 레이아웃 모드 스타일 유지
    &.layout-top {
      //탑메뉴모드일때...
      .layout-main-container {
        margin-left: 0;
        padding-top: 7.5rem;
        //transition: all 0.2s;
      }
    }
    &.layout-static {
      //사이드바 모드일때...
      .layout-main-container {
        margin-left: 16rem; // 사이드바 너비만큼 왼쪽 여백
        padding-top: 6rem; // 상단 여백 증가
        transition: all 0.2s;
      }
    }
    &.layout-static-inactive {
      .layout-main-container {
        margin-left: 0;
      }
    }
    &.layout-sidebar {
      .layout-main-container {
        padding-top: 4rem; // sidebar 메뉴일 때는 더 작은 상단 여백
        margin-left: 16rem; // 사이드바 너비만큼 마진
        transition: all 0.2s;
      }
    }
    // 전체화면 모드 스타일 추가
    &.layout-fullscreen {
      .layout-main-container {
        margin-left: 0 !important;
        padding-top: 0 !important;
      }
    }
  }
  // 전체화면 모드 헤더 스타일
  .fullscreen-header {
    padding: 0.5rem 1rem;
    background-color: var(--surface-section);
    border-bottom: 1px solid var(--surface-border);
    display: flex;
    align-items: center;
  }
  // 모바일 반응형 스타일
  @media screen and (max-width: 960px) {
    .layout-wrapper {
      &.layout-top {
        .layout-main-container {
          padding-top: 8rem; // 모바일에서는 더 큰 패딩
        }
      }
    }
  }
  @media (max-width: 768px) {
    .layout-wrapper,
    .layout-main-container,
    .layout-main {
      margin-left: 0 !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
      max-width: 100vw !important;
      width: 100vw !important;
      box-sizing: border-box;
    }
  }
</style>
